import { useEffect, useState, useCallback } from "react";
import { Sidebar } from "./components/Sidebar";
import { MockBanner } from "./components/MockBanner";
import { InitiativesPage } from "./pages/InitiativesPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CampaignsPage } from "./pages/CampaignsPage";
import { ReviewsPage } from "./pages/reviews/ReviewsPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { MallAdsPage } from "./pages/MallAdsPage";
import { LineAnalyticsPage } from "./pages/LineAnalyticsPage";
import { LoginPage } from "./pages/LoginPage";
import { PlPage } from "./pages/pl/PlPage";
import { AmazonPage } from "./pages/amazon/AmazonPage";
import { GiftingPage } from "./pages/gifting/GiftingPage";
import { ApiClientError, fetchReviewOverview, fetchSessionMe, isMockMode, logout } from "./api/client";
import { jstTodayYmd } from "./lib/dateRange";
import { onUnauthenticated } from "./lib/authState";
import { defaultScreenFor, screensFor, type SessionUser } from "./lib/roles";

export type Screen =
  | "initiatives"
  | "dashboard"
  | "campaigns"
  | "review"
  | "analytics"
  | "mall-ads"
  | "line-analytics"
  | "pl"
  | "amazon"
  | "gifting";

const SCREEN_TITLE: Record<Screen, string> = {
  initiatives: "施策",
  dashboard: "ダッシュボード",
  campaigns: "キャンペーン",
  review: "レビュー",
  analytics: "分析",
  "mall-ads": "モール広告",
  "line-analytics": "LINE分析",
  pl: "P/L",
  amazon: "Amazon商品",
  gifting: "ギフティング",
};

export default function App() {
  // T-MK-B: 施策管理をナビ先頭・既定の初期画面にする（ダッシュボードは2番目）。
  const [screen, setScreen] = useState<Screen>("initiatives");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [needsLogin, setNeedsLogin] = useState(false);
  // 役割付きログイン（tmp/fable/2026-10-20-gifting-staff-access/plan.md §3-3）。
  // undefined=確認中（何も描かない。管理者ナビを一瞬見せない）／null=未ログイン／SessionUser=確定
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const mockMode = isMockMode();

  // 実運用（非mock）で401を検知したら、以後の描画をログイン画面に切り替える（B3対応）。
  useEffect(() => onUnauthenticated(() => setNeedsLogin(true)), []);

  // 起動時に自分の役割を取る。未ログイン（401）はログイン画面へ。役割に応じて初期画面を決める。
  useEffect(() => {
    let alive = true;
    fetchSessionMe()
      .then((me) => {
        if (!alive) return;
        if (!me) {
          setUser(null);
          setNeedsLogin(true);
          return;
        }
        setUser(me);
        setScreen(defaultScreenFor(me.role));
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setSessionError(e instanceof ApiClientError ? e.message : "セッション確認に失敗しました。");
        setUser(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleLogout = useCallback(() => {
    void logout().finally(() => window.location.reload());
  }, []);

  const screens = user ? screensFor(user.role) : [];
  // 役割外の画面名を直接指定されても描かない（見た目の防御。API は BFF が 403 で止める）
  const visibleScreen: Screen = screens.includes(screen) ? screen : (user ? defaultScreenFor(user.role) : screen);

  // サイドバー「レビュー」バッジ = reviews/overview の low.open+low.wip（未対応＋対応中の低評価件数）。
  // mockモードはfixture基準月（reviewsEngine.ts NOW＝2026-09）に固定、実運用はJST当月。
  const refreshPendingCount = useCallback(() => {
    const month = isMockMode() ? "2026-09" : jstTodayYmd().slice(0, 7);
    fetchReviewOverview({ period: "month", month, store: "all" })
      .then((res) => setPendingCount(res.low.open + res.low.wip))
      .catch((e: unknown) => {
        // ナビバッジのための補助取得なので、失敗時は静かに無視する（レビュー画面本体でエラー表示済み）。
        if (e instanceof ApiClientError) return;
      });
  }, []);

  // レビューのバッジは管理者（レビュー画面を持つ役割）だけ取る。gifting 役割では /api/ad-console/* が 403 になるので呼ばない
  const canReview = screens.includes("review");
  useEffect(() => {
    if (!needsLogin && canReview) refreshPendingCount();
    // レビュー画面へ切り替えた時にも再取得する（対応完了・返信投稿などでlow.open/wipが変わりうるため）。
  }, [refreshPendingCount, needsLogin, canReview, screen]);

  if (needsLogin) {
    return (
      <>
        {mockMode && <MockBanner />}
        <LoginPage />
      </>
    );
  }

  if (user === undefined) {
    // セッション確認中。管理者ナビを一瞬見せないため何も描かない（通常 100ms 以内）
    return mockMode ? <MockBanner /> : null;
  }

  if (!user) {
    return (
      <>
        {mockMode && <MockBanner />}
        <div className="login-screen">
          <div className="login-card" role="alert">
            <div className="sidebar-brand" style={{ padding: 0, marginBottom: 4 }}>
              STOQ広告管理コンソール
            </div>
            <p style={{ color: "var(--danger)", fontSize: 13, margin: "0 0 12px" }}>{sessionError ?? "セッション確認に失敗しました。"}</p>
            <button type="button" className="btn btn-primary" style={{ width: "100%" }} onClick={() => window.location.reload()}>
              再読み込み
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {mockMode && <MockBanner />}
      <div className="app-shell">
        <div className="mobile-topbar">
          <span className="brand">STOQ広告管理コンソール</span>
          <button onClick={() => setMobileOpen((v) => !v)}>{mobileOpen ? "閉じる" : `メニュー / ${SCREEN_TITLE[visibleScreen]}`}</button>
        </div>
        <Sidebar
          screen={visibleScreen}
          onSelect={setScreen}
          pendingCount={pendingCount}
          mobileOpen={mobileOpen}
          onCloseMobile={() => setMobileOpen(false)}
          screens={screens}
          user={user}
          onLogout={handleLogout}
        />
        <main className="main">
          {visibleScreen === "initiatives" && <InitiativesPage />}
          {visibleScreen === "dashboard" && <DashboardPage />}
          {visibleScreen === "campaigns" && <CampaignsPage />}
          {visibleScreen === "review" && <ReviewsPage />}
          {visibleScreen === "analytics" && <AnalyticsPage />}
          {visibleScreen === "mall-ads" && <MallAdsPage />}
          {visibleScreen === "line-analytics" && <LineAnalyticsPage />}
          {visibleScreen === "pl" && <PlPage />}
          {visibleScreen === "amazon" && <AmazonPage />}
          {visibleScreen === "gifting" && <GiftingPage />}
        </main>
      </div>
    </>
  );
}
